import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://omwahioyabezqaomakjz.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9td2FoaW95YWJlenFhb21ha2p6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgzNDU4NjUsImV4cCI6MjA2MzkyMTg2NX0.X8BsDRC1H_7CVEtmYDQcLS_O1AM_LFaIRzk2urNAI0o';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // { email, full_name, twilio_phone_number }
  const [loading, setLoading] = useState(true);
  const fetchingProfileRef = useRef(null); // Track ongoing fetch requests
  const userProfileRef = useRef(null); // Track current userProfile for stable reference

  // Keep userProfileRef in sync with userProfile state
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  useEffect(() => {
    let mounted = true;
    async function restoreSession() {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        if (session && session.user) {
          setUser(session.user);
          fetchUserProfile(session.user.id);
        } else {
          setUser(null);
          setUserProfile(null);
        }
        setLoading(false);
      }
    }
    restoreSession();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && session.user) {
        setUser(session.user);
        fetchUserProfile(session.user.id);
      } else {
        setUser(null);
        setUserProfile(null);
        fetchingProfileRef.current = null;
      }
    });
    return () => {
      mounted = false;
      listener?.unsubscribe?.();
    };
    // eslint-disable-next-line
  }, []);

  async function fetchUserProfile(userId) {
    // Skip if we're already fetching this user's profile
    if (fetchingProfileRef.current === userId) {
      return;
    }
    
    // Skip if we already have the profile for this user (use ref for stable check)
    if (userProfileRef.current && userProfileRef.current.id === userId) {
      return;
    }
    
    fetchingProfileRef.current = userId;
    
    try {
      // Query database directly
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, twilio_phone_number, role, team_ids')
        .eq('id', userId)
        .maybeSingle();
      
      if (!error && data) {
        setUserProfile(data);
      } else if (error) {
        console.error('[AuthContext] Error fetching user profile:', error);
      } else {
        console.warn('[AuthContext] No user profile data returned for userId:', userId);
      }
    } finally {
      fetchingProfileRef.current = null;
    }
  }

  async function logout() {
    await supabase.auth.signOut();
    setUser(null);
    setUserProfile(null);
    fetchingProfileRef.current = null;
  }

  const value = {
    user,
    userProfile,
    loading,
    supabase,
    setUser,
    setUserProfile,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
} 